import DoneAllIcon from '@mui/icons-material/DoneAll';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { Box, Dialog, Divider, Grid, Stack, List, ListItemButton } from '@mui/material';
import { useState } from 'react';
import { IJob, amplitude } from 'wisestep-api';
import {
  WsButton,
  WsIconApplications,
  WsIconMapping,
  WsIconSubmitted,
  WsIconSuggestions,
  WsIconViewJD,
  WsColors,
  WsElementWithPopOver,
  WsIconAssignCandidate,
  // Item,
} from 'wisestep-base';

import { accessDeniedNotoficationTxt } from '../../../../../components/AccessControl/AccessControl';
import { ActivateFeature } from '../../../../../components/ActivateFeature';
import IconTextLink from '../../../../../components/IconTextLink';
import { useWorkspaceContext } from '../../../../../context';
import { CandidateAtsAccess, useCandidateAts } from '../../../../../hooks/useCandidateAts';
import CareerBuilder from '../../../../../images/jobBoardsIcons/CareerBuilder.png';
import Dice from '../../../../../images/jobBoardsIcons/Dice.png';
import JobServe from '../../../../../images/jobBoardsIcons/JobServe.png';
import TechFetch from '../../../../../images/jobBoardsIcons/TechFetch.png';
import { warning } from '../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../../../utils/openUrlInNewTab';
import MapAsLead from '../../../../conversationWidget/components/MapAsLead';
import UpdateProfileNew from '../../../../conversationWidget/components/UpdateProfileNew';
import { CandidateStats } from '../../JObSearchFilterDefaultQuery';
import BooleanQuery from '../booleanQuery';
import JobCountCard from '../jobsCountCard';
// import MapAsSubmission from '../mapAsSubmission/MapAsSubmission';

interface Job {
  job: IJob;
  stats?: CandidateStats;
}

const JobCardFooter = ({ job, stats }: Job) => {
  const workspace = useWorkspaceContext();
  const [openBoolean, setOpenBoolean] = useState(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const footerAmplitudeEvents = (name: string, clickedOnName: string) =>
    amplitude(name, {
      clickedOn: clickedOnName,
      workspaceName: workspace.name,
      pageName: 'JobListing',
      planType: workspace.planType,
      interfaceType: 'web',
      pageUrl: window.location.href,
    });
  const openViewJD = () => openUrlInNewTab(`/jobs-crm/${job.id}/false/0/jobDetails`);
  const openJobCrm = (tab: 'suggestions' | 'mappings' | 'submissions' | 'applications') => {
    switch (tab) {
      case 'submissions':
        openUrlInNewTab(`/jobs-crm/${job.id}/false/1/pipeline`);
        break;
      case 'applications':
        openUrlInNewTab(`/jobs-crm/${job.id}/false/2/pipeline`);
        break;
      case 'suggestions':
        openUrlInNewTab(`/jobs-crm/${job.id}/false/0/sourcing`);
        break;
      default:
        openUrlInNewTab(`/jobs-crm/${job.id}/false/0/jobDetails`);
        break;
    }
  };
  const openCandidates = () => openUrlInNewTab(`/candidates/`);
  const navigateToDice = () => openUrlInNewTab(`/native-search/dice?jobId=${job.id}`);
  const navigateToTechFetch = () => openUrlInNewTab(`native-search/techfetch?jobId=${job.id}`);
  const navigateToCareerBuilder = () =>
    openUrlInNewTab(`native-search/careerbuilder?jobId=${job.id}`);
  const navigateToJobServe = () => openUrlInNewTab(`native-search/jobServe`);

  const openBooleanQry = () => {
    footerAmplitudeEvents('Job_Listing_job_card_AIboolean_open', 'AI Boolean');
    setOpenBoolean(true);
  };
  const closeBooleanQry = () => setOpenBoolean(false);
  const [openMapAsLead, setOpenMapAsLead] = useState<boolean>(false);
  const [openSubmissionForm, setOpenSubmissionForm] = useState<boolean>(false);

  const isMapCandidate = useCandidateAts(CandidateAtsAccess.MAP_CANDIDATE);
  const isSubmitCandidate = useCandidateAts(CandidateAtsAccess.SUBMIT_CANDIDATE);

  const handleAddCandidateOptions = (optionTextToAddCandidate: 'Submit' | 'Map_As_Lead') => {
    switch (optionTextToAddCandidate) {
      case 'Submit':
        if (job?.isClosedForAssigningRecruiters) {
          setNotification(warning('Job is closed, Please reopen job to submit candidate.'));
        } else {
          footerAmplitudeEvents('Job_Listing_job_card_Add_Candidate_Submit', 'Submit');
          setOpenSubmissionForm(true);
        }
        break;
      case 'Map_As_Lead':
        if (job?.isClosedForAssigningRecruiters) {
          setNotification(warning('Job is closed, Please reopen job to map candidate.'));
        } else {
          footerAmplitudeEvents('Job_Listing_job_card_Add_Candidate_Map_as_lead', 'Map As Lead');
          setOpenMapAsLead(true);
        }
        break;
    }
  };

  const handleFindCandidateOptions = (optionTextToFindCandidate: string) => {
    switch (optionTextToFindCandidate) {
      case 'AI_Suggestions':
        footerAmplitudeEvents(
          'Job_Listing_job_card_Find_Candidates_AI_Suggestions',
          'AI Suggestions'
        );
        openUrlInNewTab(`/jobs-crm/${job.id}/false/0/sourcing`);
        break;
      case 'Talent_Pool':
        footerAmplitudeEvents('Job_Listing_job_card_Find_Candidates_Talentpool', 'Talent Pool');
        openUrlInNewTab(`/jobs-crm/${job.id}/false/2/sourcing`);
        break;
      case 'Similar_Jobs':
        footerAmplitudeEvents('Job_Listing_job_card_Find_Candidates_Similar_Job', 'Similar Jobs');
        openUrlInNewTab(`/jobs-crm/${job.id}/false/1/sourcing`);

        break;
      case 'Dice':
        footerAmplitudeEvents('Job_Listing_job_card_Find_Candidates_Dice', 'Dice');
        navigateToDice();
        break;
      case 'Career_Builder_RDB':
        footerAmplitudeEvents('Job_Listing_job_card_Find_Candidates_CB_RDB', 'Career Builder RDB');
        navigateToCareerBuilder();
        break;
      case 'Career_Builder_Edge':
        footerAmplitudeEvents(
          'Job_Listing_job_card_Find_Candidates_CB_EDGE',
          'Career Builder Edge'
        );
        openCandidates();
        break;
      case 'TechFetch':
        footerAmplitudeEvents('Job_Listing_job_card_Find_Candidates_Techfetch', 'TechFetch');
        navigateToTechFetch();
        break;
      case 'Job_Serve':
        footerAmplitudeEvents('Job_Listing_job_card_Find_Candidates_Job_Serve', 'Job Serve');
        navigateToJobServe();
        break;
    }
  };
  return (
    <Box alignItems="center">
      <Divider />
      <Stack direction="row" p={1.5}>
        <JobCountCard
          icon={<WsIconSuggestions />}
          text="AI Smart Source"
          onClick={() => {
            footerAmplitudeEvents('Job_Listing_job_card_Suggestions', 'Suggestions');
            openJobCrm('suggestions');
          }}
        />
        <JobCountCard
          icon={<WsIconMapping />}
          text="Mappings"
          count={stats?.mapped ?? 0}
          onClick={() => {
            footerAmplitudeEvents('Job_Listing_job_card_Mapping', 'Mappings');
            openJobCrm('mappings');
          }}
        />
        <JobCountCard
          icon={<WsIconSubmitted />}
          text="Submitted"
          count={stats?.submitted ?? 0}
          onClick={() => {
            footerAmplitudeEvents('Job_Listing_job_card_Submission', 'Submitted');
            openJobCrm('submissions');
          }}
        />
        <JobCountCard
          icon={<WsIconApplications />}
          text="Applications"
          count={stats?.applied ?? 0}
          onClick={() => {
            footerAmplitudeEvents('Job_Listing_job_card_Application', 'Applications');
            openJobCrm('applications');
          }}
        />
      </Stack>
      <Box sx={{ backgroundColor: WsColors.TheBlues25 }} p={1.5}>
        <Grid container display="flex" justifyContent="space-evenly">
          <WsButton
            className="ai-boolean"
            variant="outlined"
            onClick={() => {
              footerAmplitudeEvents('Job_Listing_job_card_AIboolean', 'AI Boolean');
              openBooleanQry();
            }}
          >
            Gen. Boolean String
          </WsButton>
          <Dialog
            onClose={closeBooleanQry}
            aria-labelledby="Boolean qry"
            open={openBoolean}
            fullWidth
            maxWidth="lg"
          >
            <BooleanQuery closeDialog={closeBooleanQry} job={job} />
          </Dialog>
          <WsButton
            variant="outlined"
            startIcon={<WsIconViewJD />}
            onClick={() => {
              openViewJD();
              footerAmplitudeEvents('Job_Listing_job_card_view_details ', 'View Details');
            }}
          >
            View Details
          </WsButton>
          <WsElementWithPopOver
            element={
              <WsButton
                className="find-candidates"
                variant="contained"
                endIcon={<KeyboardArrowDownOutlinedIcon />}
              >
                Find Candidates
              </WsButton>
            }
          >
            <List dense={true} sx={{ pl: 1 }}>
              <ActivateFeature feature="CS_CANDIDATE_CV_RECOMMENDATIONS">
                <ListItemButton onClick={() => handleFindCandidateOptions('AI_Suggestions')}>
                  <IconTextLink text="AI Suggestions" icon={<WsIconAssignCandidate />} />
                </ListItemButton>
              </ActivateFeature>
              <ListItemButton onClick={() => handleFindCandidateOptions('Talent_Pool')}>
                <IconTextLink text="Talent Pool" icon={<WsIconAssignCandidate />} />
              </ListItemButton>
              <ActivateFeature feature="CS_SIMILAR_JOBS">
                <ListItemButton onClick={() => handleFindCandidateOptions('Similar_Jobs')}>
                  <IconTextLink text="Similar Jobs" icon={<WsIconAssignCandidate />} />
                </ListItemButton>
              </ActivateFeature>
              <ListItemButton onClick={() => handleFindCandidateOptions('Dice')}>
                <IconTextLink
                  text="Dice"
                  icon={<img src={Dice} alt="Dice" height={25} width={25} />}
                />
              </ListItemButton>
              <ListItemButton onClick={() => handleFindCandidateOptions('Career_Builder_RDB')}>
                <IconTextLink
                  text="Career Builder RDB"
                  icon={<img src={CareerBuilder} alt="Career Builder" height={25} width={25} />}
                />
              </ListItemButton>
              <ListItemButton onClick={() => handleFindCandidateOptions('Career_Builder_Edge')}>
                <IconTextLink
                  text="Career Builder Edge"
                  icon={<img src={CareerBuilder} alt="Career Builder" height={25} width={25} />}
                />
              </ListItemButton>
              <ListItemButton onClick={() => handleFindCandidateOptions('TechFetch')}>
                <IconTextLink
                  text="TechFetch"
                  icon={<img src={TechFetch} alt="Tech Fetch" height={25} width={25} />}
                />
              </ListItemButton>
              <ListItemButton onClick={() => handleFindCandidateOptions('Job_Serve')}>
                <IconTextLink
                  text="Job Serve"
                  icon={<img src={JobServe} alt="Job Serve" height={25} width={25} />}
                />
              </ListItemButton>
            </List>
          </WsElementWithPopOver>
          <WsButton
            onClick={() =>
              isMapCandidate
                ? handleAddCandidateOptions('Map_As_Lead')
                : setNotification(warning(accessDeniedNotoficationTxt))
            }
            startIcon={<WsIconAssignCandidate color={WsColors.White} height={25} />}
          >
            Add a Lead
          </WsButton>
          <WsButton
            onClick={() =>
              isSubmitCandidate
                ? handleAddCandidateOptions('Submit')
                : setNotification(warning(accessDeniedNotoficationTxt))
            }
            startIcon={<DoneAllIcon />}
          >
            Submit Candidate
          </WsButton>
        </Grid>
      </Box>
      {openMapAsLead && (
        <MapAsLead
          candidate={null}
          job={job}
          close={() => setOpenMapAsLead(false)}
          hideJobSelection={true}
        />
      )}
      {openSubmissionForm && job && (
        <UpdateProfileNew
          candidate={null}
          openUpdatePanel={openSubmissionForm}
          closeUpdatePanel={() => setOpenSubmissionForm(false)}
          job={job}
          hideJobSelection={true}
          heading="Submit Candidate"
          isSubmit={true}
        />
        // <>
        //   <WsDrawer
        //     style={{ zIndex: '1300' }}
        //     size="medium"
        //     hideHeader={true}
        //     open={openSubmissionForm}
        //     onClose={() => setOpenSubmissionForm(false)}
        //     hideOverflow={true}
        //   >
        //     <MapAsSubmission selectedJob={job} onClose={() => setOpenSubmissionForm(false)} />
        //   </WsDrawer>
        // </>
      )}
    </Box>
  );
};

export default JobCardFooter;
